import { Clock, Droplet, FileText, Package, Search, Ship, Truck } from 'lucide-react';
import type { TaskTypeValue } from '@my/tms-api';
import { adaptTaskType } from './adapters';
import type { TaskType } from './types';

/**
 * Three-colour task badge rule:
 *   • Green  — load-side cargo handling (Pickup, Load, Collect).
 *   • Blue   — delivery-side cargo handling (Drop-off, Unload, Deliver).
 *   • Purple — booking halves (Boarding, Disembark).
 *   • Yellow — process events (Stop, Instruction, Inspection, and BE
 *              specialty types collapsed via `adaptTaskType`).
 */
const TONE_GREEN = {
  tone: 'border-emerald-300 bg-emerald-50 text-emerald-700',
  dot: 'bg-emerald-500',
};
const TONE_BLUE = {
  tone: 'border-blue-300 bg-blue-50 text-blue-700',
  dot: 'bg-blue-500',
};
const TONE_PURPLE = {
  tone: 'border-purple-300 bg-purple-50 text-purple-700',
  dot: 'bg-purple-500',
};
const TONE_YELLOW = {
  tone: 'border-amber-300 bg-amber-50 text-amber-700',
  dot: 'bg-amber-500',
};

export const TASK_TYPE_CONFIG: Record<
  TaskType,
  { icon: typeof Truck; tone: string; dot: string; label: string }
> = {
  PickUp: { icon: Package, ...TONE_GREEN, label: 'Pickup' },
  Load: { icon: Droplet, ...TONE_GREEN, label: 'Load' },
  Collect: { icon: Package, ...TONE_GREEN, label: 'Collect' },
  DropOff: { icon: Truck, ...TONE_BLUE, label: 'Drop-off' },
  Unload: { icon: Droplet, ...TONE_BLUE, label: 'Unload' },
  Deliver: { icon: Truck, ...TONE_BLUE, label: 'Deliver' },
  BookingDeparture: { icon: Ship, ...TONE_PURPLE, label: 'Boarding' },
  BookingArrival: { icon: Ship, ...TONE_PURPLE, label: 'Disembark' },
  Stop: { icon: Clock, ...TONE_YELLOW, label: 'Stop' },
  Instruction: { icon: FileText, ...TONE_YELLOW, label: 'Instruction' },
  Inspection: { icon: Search, ...TONE_YELLOW, label: 'Inspection' },
};

/**
 * Operator-facing labels for the BE `TaskTypeValue` enum. Booking
 * halves are intentionally omitted — they're spawned via
 * `useCreateOrderBooking` which creates the booking + paired tasks
 * atomically; the create-task form only creates standalone tasks.
 */
export const TASK_TYPE_LABELS_BE: Partial<Record<TaskTypeValue, string>> = {
  PickUp: 'Pickup',
  DropOff: 'Drop-off',
  Collect: 'Collect',
  Deliver: 'Deliver',
  Load: 'Load',
  Unload: 'Unload',
  Instruction: 'Instruction',
  Stop: 'Stop',
  Inspection: 'Inspection',
  TempCheck: 'Temperature check',
  ReeferInspection: 'Reefer inspection',
  SampleCheck: 'Sample check',
  Weigh: 'Weigh',
  Wash: 'Wash',
  TankClean: 'Tank clean',
  VetCheck: 'Vet check',
  BorderInspection: 'Border inspection',
  PhytosanitaryCheck: 'Phytosanitary check',
  FumigationStop: 'Fumigation stop',
  PermitStop: 'Permit stop',
  SiteCheck: 'Site check',
  EscortStart: 'Escort start',
  FeedWaterStop: 'Feed / water stop',
};

/**
 * What to CALL a task: its own type ("Wash", "Tank clean", "Fumigation stop"),
 * not the display bucket `adaptTaskType` folds it into for the icon and
 * colour — the bucket is for styling, and naming a Wash "Inspection" read as
 * the type having been saved wrong.
 */
export function taskTypeLabel(type: TaskTypeValue): string {
  return TASK_TYPE_LABELS_BE[type] ?? TASK_TYPE_CONFIG[adaptTaskType(type)]?.label ?? type;
}
